/**
 * المرحلة 1.3 (P10) — «ملفي المهني»: what a patient reads on the doctor's page.
 *
 * The bio in Arabic and in English, the languages, the qualifications, the
 * years of experience and the channels the doctor offers (inside what their
 * institution's consultModes allows). GET/PUT /api/doctors/profile carry it
 * with `professional`: the fields the doctorOnboarding policy requires before
 * patients see the doctor, what is still missing, and whether a reviewer
 * publishes it first. By default nothing is required and nothing is
 * reviewed: the doctor is listed as before and this page only adds words.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ErrorState, Field, Icon, Input, PageHeader, Panel, Spinner, Textarea } from '../../components/common';
import { doctorApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { LANGUAGE_CODES, MODES } from '../../domain/discovery';
import { useI18n } from '../../i18n/I18nProvider';
import { ModeBadge } from '../discovery/parts';
import { useAuth } from '../../context/AuthContext';
import { DoctorProfilePage } from '../patient/DoctorProfilePage';

const blankQual = () => ({ title: '', place: '', year: '' });

export function DoctorProfessionalProfile() {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    doctorApi
      .profile()
      .then((data) => {
        const p = data.DoctorProfile || {};
        setForm({
          bioAr: p.bioAr || '',
          bioEn: p.bioEn || '',
          languages: Array.isArray(p.languages) ? p.languages : [],
          qualifications: Array.isArray(p.qualifications) && p.qualifications.length ? p.qualifications.map((q) => ({ ...blankQual(), ...q, year: q.year || '' })) : [blankQual()],
          yearsOfExperience: p.yearsOfExperience ?? '',
          consultModes: Array.isArray(p.consultModes) && p.consultModes.length ? p.consultModes : ['in_person'],
        });
        setState({ loading: false, error: null, data });
      })
      .catch((error) => setState({ loading: false, error, data: null }));
  };
  useEffect(load, []);

  if (state.loading && !state.data) return <Spinner label={t('proProfile.loading')} />;
  if (state.error) return <ErrorState error={state.error.message} onRetry={load} />;

  const profile = state.data.DoctorProfile || {};
  const pro = state.data.professional || { required: [], missing: [], review: 'none', shown: true, modesAllowed: ['in_person'] };
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = (key, value) => setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value] }));

  const save = async () => {
    setSaving(true);
    try {
      const result = await doctorApi.updateProfile({
        bioAr: form.bioAr,
        bioEn: form.bioEn,
        languages: form.languages,
        qualifications: form.qualifications.filter((q) => q.title.trim()).map((q) => ({ ...q, year: q.year ? Number(q.year) : null })),
        yearsOfExperience: form.yearsOfExperience === '' ? null : Number(form.yearsOfExperience),
        consultModes: form.consultModes,
      });
      success(result?.profile?.profileStatus === 'review' && profile.profileStatus !== 'review' ? t('proProfile.sentForReview') : t('proProfile.saved'));
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const status = pro.review === 'review' ? profile.profileStatus || 'draft' : null;

  return (
    <div className="space-y-6" data-testid="pro-profile">
      <PageHeader
        title={t('proProfile.title')}
        subtitle={t('proProfile.subtitle')}
        icon="badge-check"
        actions={
          <Link to="/doctor/profile/preview" className="btn-ghost">
            <Icon name="eye" size={16} />
            {t('proProfile.preview')}
          </Link>
        }
      />

      <Panel>
        <div className="flex flex-wrap items-center gap-2">
          {pro.shown ? (
            <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="check-circle-2">
              {t('proProfile.shown')}
            </Badge>
          ) : (
            <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="eye-off">
              {t('proProfile.hidden')}
            </Badge>
          )}
          {status && <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{t(`proProfile.status.${status}`)}</Badge>}
        </div>
        {pro.missing.length > 0 && (
          <p className="reason-note mt-3">
            <Icon name="alert-triangle" size={14} className="mt-0.5 shrink-0" />
            <span>{t('proProfile.missing', { fields: pro.missing.map((f) => t(`proProfile.field.${f}`)).join(t('proProfile.joiner')) })}</span>
          </p>
        )}
        {pro.review === 'review' && status !== 'published' && (
          <p className="info-note mt-3">
            <Icon name="info" size={14} className="mt-0.5 shrink-0" />
            <span>{t('proProfile.reviewNote')}</span>
          </p>
        )}
        {pro.required.includes('photo') && (
          <p className="mt-3 text-xs text-slate-400">
            {t('proProfile.photoHint')}{' '}
            <Link to="/doctor/settings" className="font-bold text-cyan-300 hover:underline">
              {t('proProfile.photoLink')}
            </Link>
          </p>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel className="space-y-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name="user-round" size={18} className="text-cyan-400" />
            {t('proProfile.about')}
            {pro.required.includes('bio') && <span className="text-[10px] text-amber-300">{t('proProfile.requiredTag')}</span>}
          </h3>
          <Field label={t('proProfile.bioAr')}>
            <Textarea rows={5} maxLength={2000} dir="rtl" value={form.bioAr} onChange={(e) => set('bioAr', e.target.value)} data-testid="bio-ar" />
          </Field>
          <Field label={t('proProfile.bioEn')}>
            <Textarea rows={5} maxLength={2000} dir="ltr" value={form.bioEn} onChange={(e) => set('bioEn', e.target.value)} />
          </Field>
          <Field label={t('proProfile.experience')} required={pro.required.includes('experience')}>
            <Input type="number" min={0} max={70} value={form.yearsOfExperience} onChange={(e) => set('yearsOfExperience', e.target.value)} data-testid="experience" />
          </Field>
        </Panel>

        <Panel className="space-y-4">
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="languages" size={16} className="text-purple-400" />
              {t('proProfile.languages')}
              {pro.required.includes('languages') && <span className="text-[10px] text-amber-300">{t('proProfile.requiredTag')}</span>}
            </p>
            <div className="dc-chips">
              {LANGUAGE_CODES.map((code) => (
                <button key={code} type="button" className={`dc-chip ${form.languages.includes(code) ? 'is-on' : ''}`} aria-pressed={form.languages.includes(code)} onClick={() => toggle('languages', code)}>
                  {t(`doctorPage.lang.${code}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="video" size={16} className="text-violet-400" />
              {t('proProfile.modes')}
            </p>
            <div className="dc-chips">
              {MODES.filter((m) => pro.modesAllowed.includes(m)).map((m) => (
                <button key={m} type="button" className={`dc-chip ${form.consultModes.includes(m) ? 'is-on' : ''}`} aria-pressed={form.consultModes.includes(m)} onClick={() => toggle('consultModes', m)} data-testid={`offer-${m}`}>
                  <ModeBadge mode={m} compact className="!border-0 !bg-transparent !p-0" />
                  {t(`discovery.mode.${m}`)}
                </button>
              ))}
            </div>
            {pro.modesAllowed.length === 1 && <p className="mt-2 text-[11px] text-slate-500">{t('proProfile.modesOnlyInPerson')}</p>}
          </div>

          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="graduation-cap" size={16} className="text-amber-400" />
              {t('proProfile.qualifications')}
              {pro.required.includes('qualifications') && <span className="text-[10px] text-amber-300">{t('proProfile.requiredTag')}</span>}
            </p>
            <div className="space-y-2">
              {form.qualifications.map((q, index) => (
                <div key={index} className="grid grid-cols-1 gap-2 rounded-xl bg-white/5 p-2 sm:grid-cols-[2fr_2fr_1fr_auto]">
                  <Input placeholder={t('proProfile.qualTitle')} aria-label={t('proProfile.qualTitle')} value={q.title} onChange={(e) => set('qualifications', form.qualifications.map((x, i) => (i === index ? { ...x, title: e.target.value } : x)))} />
                  <Input placeholder={t('proProfile.qualPlace')} aria-label={t('proProfile.qualPlace')} value={q.place || ''} onChange={(e) => set('qualifications', form.qualifications.map((x, i) => (i === index ? { ...x, place: e.target.value } : x)))} />
                  <Input placeholder={t('proProfile.qualYear')} aria-label={t('proProfile.qualYear')} type="number" value={q.year} onChange={(e) => set('qualifications', form.qualifications.map((x, i) => (i === index ? { ...x, year: e.target.value } : x)))} />
                  <Button variant="subtle" icon="trash-2" aria-label={t('proProfile.remove')} onClick={() => set('qualifications', form.qualifications.filter((_, i) => i !== index))} />
                </div>
              ))}
              <Button variant="ghost" icon="plus" onClick={() => set('qualifications', [...form.qualifications, blankQual()])}>
                {t('proProfile.addQual')}
              </Button>
            </div>
          </div>
        </Panel>
      </div>

      <div className="flex justify-end">
        <Button icon="save" onClick={save} loading={saving} data-testid="pro-save">
          {pro.review === 'review' && status === 'draft' ? t('proProfile.saveAndSend') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}

export default DoctorProfessionalProfile;

/** /doctor/profile/preview — the doctor's own page, as a patient will read it. */
export function DoctorProfilePreview() {
  const { doctorId } = useAuth();
  return <DoctorProfilePage previewId={doctorId} />;
}
